import { getField, updateField } from 'vuex-map-fields';
import { getSettingsFormConfig, getFormValidations } from '~/config/formConfig';

const defaultState = () => ({
  activeSettings: 'generalSettingsForm',
  baseCurrency: 'CZK',
  canDisableFooterBrand: false,
  dialogOpened: false,
  disableBrandInFooter: false,
  errorMessage: null,
  invoiceStateEnabled: false,
  lineSuggestionsEnabled: false,
  predictNumber: null,
  predictType: null,
  predictDate: null,
});

const mutations = {
  updateField,
  openDialog: (state) => {
    state.dialogOpened = true;
  },
  resetState(state) {
    Object.assign(state, defaultState());
  },
  setActiveTab: (state, activeTab) => {
    state.activeTab = activeTab;
  },
  setErrorMessage: (state, message) => {
    state.errorMessage = message;
  },
  openSettingsDialog(state) {
    state.dialogOpened = true;
  },
  closeSettingsDialog(state) {
    state.dialogOpened = false;
  },
  getSettingsLoaded(state, data) {
    state.disableBrandInFooter = data.disable_brand_in_footer;
    state.invoiceStateEnabled = data.invoice_state;
    state.lineSuggestionsEnabled = data.invoice_line_descriptions;
    state.canDisableFooterBrand = data.can_disable_footer_brand;
    state.baseCurrency = data.base_currency;
  },
  getSettingsLoadError() {},
  setActiveSettings(state, name) {
    state.activeSettings = name;
  },
  predictPattern(state, { number, type, date = null }) {
    state.predictNumber = number;
    state.predictType = type;
    state.predictDate = date;
  },
};

const actions = {
  closeDialog: ({ commit }) => {
    commit('resetState');
  },
  changeColumnsSettings: ({ commit, rootGetters }, { scope, columns }) => {
    commit(
      'api/fetch',
      {
        url: '/settings',
        method: 'PATCH',
        headers: {
          ...rootGetters['auth/getHeaders'],
        },
        body: {
          setting: {
            table_columns: {
              [scope]: columns,
            },
          },
        },
        onSuccess: ['settingsForm/changeColumnsSuccess'],
        tableColumns: columns,
      },
      { root: true },
    );
  },
  submitForm: ({ commit, rootGetters, state }, { marAsPaid }) => {
    commit(
      'api/fetch',
      {
        url: '/settings',
        method: 'PATCH',
        headers: {
          ...rootGetters['auth/getHeaders'],
        },
        body: {
          setting: {
            disable_brand_in_footer: state.disableBrandInFooter,
            invoice_state: state.invoiceStateEnabled,
            invoice_line_descriptions: state.lineSuggestionsEnabled,
            update_all_invoices: marAsPaid,
            base_currency: state.baseCurrency,
          },
        },
        onSuccess: ['settingsForm/formSubmitSuccess'],
        onError: ['settingsForm/formSubmitError'],
        marAsPaid,
      },
      { root: true },
    );
  },
  formSubmitError: ({ commit }) => {
    commit('setErrorMessage', 'storeSettingsError');
  },
  changeColumnsSuccess({ _ }, action) {
    this.dispatch('config/setColumnsConfig', action.payload.config.tableColumns, { root: true });
  },
  formSubmitSuccess({ commit }, action) {
    const { marAsPaid } = action.payload.meta;

    this.dispatch('flashMessages/addMessage', {
      kind: 'success',
      key: 'settingsSavedSuccessful',
      params: action.payload.data,
    });

    this.dispatch('config/setInvoicesStateConfig', action.payload.data.invoice_state, {
      root: true,
    });

    this.dispatch('config/setLineSuggestionConfig', action.payload.data.invoice_line_descriptions, {
      root: true,
    });

    this.dispatch('config/setInvoicesWithoutState', !marAsPaid, { root: true });

    this.dispatch('api/refetchInvoices', { root: true });

    this.dispatch('uiEffects/scrollTo', {
      top: 0,
      left: 0,
    });

    commit('resetState');
  },
  getSettingsLoaded: ({ commit }, action) => {
    commit('getSettingsLoaded', action.payload.data);
    commit('openSettingsDialog');
  },
  getSettingsLoadError: ({ commit }, action) => {
    commit('getSettingsLoadError', action);
  },
  openSettingsDialog: ({ commit, rootGetters }, { type }) => {
    commit('setActiveSettings', type);

    if (type === 'generalSettingsForm') {
      commit(
        'api/fetch',
        {
          url: '/settings/edit',
          method: 'GET',
          headers: {
            ...rootGetters['auth/getHeaders'],
          },
          onSuccess: ['settingsForm/getSettingsLoaded'],
          onError: ['settingsForm/getSettingsLoadError'],
        },
        { root: true },
      );
    } else {
      commit('openSettingsDialog');
    }
  },
  predictPattern: ({ commit }, number, type) => {
    commit('predictPattern', number, type);
  },
  setSequencesToForm({ _ }, action) {
    this.dispatch('invoiceForm/setSequences', action, { root: true });
  },
};

/* eslint no-shadow: ["error", { "allow": ["getters"] }] */

const getters = {
  getField,
  validationRules: state => getFormValidations(getSettingsFormConfig(state)),
  canDisableFooterBrand: state => state.canDisableFooterBrand,
  settingsForm: state => state,
};

export default {
  namespaced: true,
  state: defaultState(),
  getters,
  actions,
  mutations,
};
